import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from "react-router-dom";

import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import { Table } from '../../models/interfaces';
import { TablesContext } from '../../context/table/TableContext';
import QRGenerator from './QRPage';


const PAGE_SIZE = 10;
const NO_SECTION = -1;

export const TablesPage = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const [activeSection, setActiveSection] = useState<number | 'all'>('all');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingQrId, setLoadingQrId] = useState<number | null>(null);

    const [showQR, setShowQR] = useState(false);
    const [qrValue, setQrValue] = useState('');
    const [title, setTitle] = useState('');

    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState('');
    const [tableId, setTableId] = useState<number | null>(null);

    const { tables, loadTables, deleteTable, getTableToken } = useContext(TablesContext);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                await loadTables(0);
            } catch (e) {
                setError('No se pudieron cargar las mesas, intente de nuevo.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [])

    useEffect(() => {
        setPageActual(1);
    }, [buscar, activeSection])

    // Pestañas por sección con su conteo
    const sectionTabs = useMemo(() => {
        const map = new Map<number, { id: number; name: string; count: number }>();
        tables.forEach(table => {
            const id = table.section?.id ?? NO_SECTION;
            const name = table.section?.name ?? 'Sin sección';
            if (!map.has(id)) map.set(id, { id, name, count: 0 });
            map.get(id)!.count += 1;
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [tables]);

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        return tables
            .filter(table => activeSection === 'all' || (table.section?.id ?? NO_SECTION) === activeSection)
            .filter(table => !term ||
                String(table.name ?? '').toLowerCase().includes(term) ||
                String(table.section?.name ?? '').toLowerCase().includes(term));
    }, [tables, buscar, activeSection]);

    const totalRecords = filteredData.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageItems = filteredData.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);

    // Si al eliminar se reduce el número de páginas, se ajusta la página actual
    useEffect(() => {
        if (pageActual > totalPage) setPageActual(totalPage);
    }, [totalPage])

    // Si se elimina la última mesa de la sección seleccionada, se vuelve a "Todas"
    useEffect(() => {
        if (activeSection !== 'all' && !sectionTabs.some(tab => tab.id === activeSection)) {
            setActiveSection('all');
        }
    }, [sectionTabs])

    const handleNew = () => {
        navigate("/table");
    }

    const handleEdit = (table: Table) => {
        navigate("/table", { state: { table } });
    }

    const handleQr = async (table: Table) => {
        setLoadingQrId(table.id);
        const token = await getTableToken(table.id);
        setLoadingQrId(null);
        if (token != "Ocurrió un error de comunicación, intente de nuevo.") {
            setQrValue(process.env.REACT_APP_MENU_QR + "/menuqr/" + token);
            setTitle(table.name);
            setShowQR(true)
        } else {
            setError(token);
        }
    }

    const handleDelete = (table: Table) => {
        setTableId(table.id);
        setConfirm(`¿Está seguro que desea eliminar la mesa "${table.name}"?`);
    }

    const processDelete = async () => {
        setConfirm('');
        if (tableId === null) return;
        const resp = await deleteTable(tableId);
        if (resp !== "success") {
            setError(resp);
        }
        setTableId(null);
    }

    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const removeLocalError = () => setError('');
    const removeConfirmMessage = () => { setConfirm(''); setTableId(null); };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Mesas</h3>
                    <p className="text-muted mb-0">Administre las mesas de su local y genere el código QR del menú de cada una.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/sections')}>
                        <i className="bi bi-grid-3x3-gap me-1" /> Secciones
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/tables/layout')}>
                        <i className="bi bi-bounding-box me-1" /> Plano de mesas
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Agregar mesa
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    {/* Tabs de sección */}
                    {sectionTabs.length > 0 && (
                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeSection === 'all' ? 'active' : ''}`}
                                    onClick={() => setActiveSection('all')}
                                >
                                    Todas ({tables.length})
                                </button>
                            </li>
                            {sectionTabs.map(tab => (
                                <li className="nav-item" key={tab.id}>
                                    <button
                                        className={`nav-link ${activeSection === tab.id ? 'active' : ''}`}
                                        onClick={() => setActiveSection(tab.id)}
                                    >
                                        {tab.name} ({tab.count})
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por mesa o sección..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table id='tablesTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Mesa</th>
                                    <th>Sección</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={3} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : pageItems.length > 0 ? (
                                    pageItems.map(table => (
                                        <tr key={table.id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={styles.iconCircle}>
                                                        <i className="bi bi-square" />
                                                    </div>
                                                    <span className="fw-medium">{table.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                {table.section
                                                    ? <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">{table.section.name}</span>
                                                    : <span className="text-muted">-</span>}
                                            </td>
                                            <td style={{ textAlign: 'center', width: 150 }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-success"
                                                    title="Código QR del menú"
                                                    disabled={loadingQrId === table.id}
                                                    onClick={() => handleQr(table)}
                                                >
                                                    {loadingQrId === table.id
                                                        ? <span className="spinner-border spinner-border-sm" style={{ width: 14, height: 14 }} />
                                                        : <i className="bi bi-qr-code" />}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary ms-1"
                                                    title="Editar mesa"
                                                    onClick={() => handleEdit(table)}
                                                >
                                                    <i className="bi bi-pencil" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger ms-1"
                                                    title="Eliminar mesa"
                                                    onClick={() => handleDelete(table)}
                                                >
                                                    <i className="bi bi-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={3} className='text-center py-4 text-muted'>
                                        {buscar ? 'No se encontraron mesas para la búsqueda.' : 'No existen mesas.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <span className="text-muted small">
                            {totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} mesas` : ''}
                        </span>
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {showQR && <QRGenerator qrValue={qrValue} texto={title} closeDialog={() => setShowQR(false)} />}

            {error.length > 0 && (
                <Message
                    type="error"
                    message={error}
                    closeDialog={removeLocalError}
                    handleOk={removeLocalError}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {confirm.length > 0 && (
                <Message
                    type="success"
                    title="Atención"
                    message={confirm}
                    closeDialog={removeConfirmMessage}
                    showCancel={true}
                    handleCancel={removeConfirmMessage}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    iconCircle: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e7f1ff',
        color: '#0d6efd',
        flexShrink: 0,
    },
};
